import React, { useState } from "react";

import { ConfirmDialog } from "@@components";
import { AddCircle } from "@mui/icons-material";
import { Box, Button, CircularProgress, List, ListItem, Paper, Stack, Typography } from "@mui/material";

import { EnvironmentServiceConnectionModel, ServiceConnectionModel } from "../../../api/api";
import ServiceConnectionDisplay from "./ServiceConnectionDisplay";

interface ServiceConnectionsListProps {
	mainServiceConnection?: ServiceConnectionModel | null;
	serviceConnections: EnvironmentServiceConnectionModel[];
	serviceConnectionDetails?: ServiceConnectionModel[];
	onLink: () => void;
	onUnlink: (serviceConnectionId: string) => void;
	mainTitle?: string;
	title?: string;
	emptyMessage?: string;
	isLoading?: boolean;
	isLoadingDetails?: boolean;
	showMainConnection?: boolean;
}

export default function ServiceConnectionsList({
	mainServiceConnection = null,
	serviceConnections,
	serviceConnectionDetails = [],
	onLink,
	onUnlink,
	mainTitle = "Cloud Connection",
	title = "Service Connections",
	emptyMessage = "No service connections associated with this environment",
	isLoading = false,
	isLoadingDetails = false,
	showMainConnection = true,
}: ServiceConnectionsListProps) {
	const [confirmDialog, setConfirmDialog] = useState<{
		open: boolean;
		serviceConnectionData: (EnvironmentServiceConnectionModel & { details: ServiceConnectionModel | null }) | null;
	}>({
		open: false,
		serviceConnectionData: null,
	});

	// Combine service connections with their details
	const serviceConnectionsWithDetails = serviceConnections.map((sc) => {
		const details = serviceConnectionDetails.find((detail) => detail.id === sc.serviceConnectionId);
		return { ...sc, details: details || null };
	});
	const handleUnlinkClick = (serviceConnectionData: EnvironmentServiceConnectionModel & { details: any }) => {
		setConfirmDialog({
			open: true,
			serviceConnectionData,
		});
	};

	const handleConfirmUnlink = () => {
		if (confirmDialog.serviceConnectionData) {
			onUnlink(confirmDialog.serviceConnectionData.serviceConnectionId);
		}
		setConfirmDialog({ open: false, serviceConnectionData: null });
	};

	const handleCancelUnlink = () => {
		setConfirmDialog({ open: false, serviceConnectionData: null });
	};

	return (
		<>
			{/* Main Service Connection */}
			{showMainConnection && mainServiceConnection && (
				<Box sx={{ mb: 3 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
						{mainTitle}
					</Typography>
					<Paper sx={{ bgcolor: "primary.50", border: "1px solid", borderColor: "primary.200" }}>
						<ServiceConnectionDisplay serviceConnection={mainServiceConnection} isLoading={isLoadingDetails} />
					</Paper>
				</Box>
			)}

			{/* Associated Service Connections */}
			<Box sx={{ mb: 3 }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
						{title}
					</Typography>
					<Button
						variant="outlined"
						size="small"
						startIcon={isLoading ? <CircularProgress size={16} /> : <AddCircle />}
						onClick={onLink}
						disabled={isLoading}
						title="Link service connection"
					>
						{isLoading ? "Updating..." : "Link"}
					</Button>
				</Stack>

				{serviceConnections.length === 0 ? (
					<Paper sx={{ p: 2, textAlign: "center", bgcolor: "grey.50" }}>
						<Typography variant="body2" color="textSecondary">
							{emptyMessage}
						</Typography>
					</Paper>
				) : (
					<Paper sx={{ border: "1px solid #e0e0e0" }}>
						<List>
							{serviceConnectionsWithDetails.map((serviceConnectionData, index) => {
								const { details } = serviceConnectionData;
								return (
									<ListItem key={serviceConnectionData.id} disablePadding divider={index < serviceConnectionsWithDetails.length - 1}>
										<Box sx={{ px: 2, py: 1.5, width: "100%" }}>
											<ServiceConnectionDisplay
												serviceConnection={details}
												serviceConnectionId={serviceConnectionData.serviceConnectionId}
												serviceConnectionType={serviceConnectionData.serviceConnectionType}
												isLoading={isLoadingDetails}
												onUnlink={() => handleUnlinkClick(serviceConnectionData)}
												isUnlinking={isLoading}
												showUnlinkButton={true}
											/>
										</Box>
									</ListItem>
								);
							})}
						</List>
					</Paper>
				)}
			</Box>

			<ConfirmDialog
				open={confirmDialog.open}
				title="Unlink Service Connection"
				message={
					confirmDialog.serviceConnectionData
						? `Are you sure you want to unlink the service connection "${
								confirmDialog.serviceConnectionData.details?.name || confirmDialog.serviceConnectionData.serviceConnectionId
							}" from this environment?`
						: ""
				}
				confirmText="Unlink"
				confirmColor="error"
				onConfirm={handleConfirmUnlink}
				onCancel={handleCancelUnlink}
			/>
		</>
	);
}
